import { clsx, type ClassValue } from "clsx"
import { format, formatDistanceToNowStrict, isValid, parseISO } from "date-fns"
import parseJson from "json-parse-even-better-errors" // eslint-disable-line no-restricted-imports -- intentional as this is used internally
import { twMerge } from "tailwind-merge"

import { hasTenantRole } from "@/lib/user-utils"
import { type CurrentUser } from "@/services/users/user.types"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Safely parse JSON string, returning typed result on success or fallback on failure.
 * Use this instead of try/catch around JSON.parse in React components
 * to avoid JSX-in-try-catch lint errors.
 *
 * @param data - JSON string to parse
 * @param fallback - Value to return on parse failure (defaults to undefined)
 */
// eslint-disable-next-line @typescript-eslint/no-unnecessary-type-parameters
export function safeJsonParse<T, F = undefined>(
  data: string | undefined,
  fallback?: F
): T | F {
  if (data === undefined) {
    return fallback as F
  }
  const result = parseJson.noExceptions<T>(data)
  // Explicit undefined check: noExceptions returns undefined on failure, null for valid "null"
  return (result === undefined ? fallback : result) as T | F
}

/**
 * Parse JSON string with better error messages.
 * Uses json-parse-even-better-errors for improved error context and BOM stripping.
 * Throws on parse failure with detailed error information.
 *
 * @param data - JSON string to parse
 * @throws JSONParseError with position and context on failure
 */
// eslint-disable-next-line @typescript-eslint/no-unnecessary-type-parameters
export function jsonParse<T>(data: string): T {
  return parseJson<T>(data)
}

/**
 * Safely stringify value to JSON, returning fallback on failure (e.g., circular references).
 * Use this instead of try/catch around JSON.stringify in React components
 * to avoid JSX-in-try-catch lint errors.
 *
 * @param value - Value to stringify
 * @param options.space - Indentation (default: undefined)
 * @param options.fallback - Value to return on stringify failure (default: "")
 */
export function safeJsonStringify(
  value: unknown,
  options?: { space?: number; fallback?: string }
): string {
  try {
    const result = JSON.stringify(value, null, options?.space)
    // JSON.stringify returns undefined for undefined, functions, and symbols
    return result ?? options?.fallback ?? ""
  } catch {
    return options?.fallback ?? ""
  }
}

function isEnv(envName: string) {
  return window.settings.ENV_NAME === envName
}

/** @knipignore */
export function isDemoEnv() {
  return isEnv("demo")
}

/** @knipignore */
export function isDev1Env() {
  return isEnv("dev1")
}

export function isLocalDevelopment() {
  return window.settings.ENV_NAME.startsWith("development")
}

export function isMac(): boolean {
  return (
    typeof navigator !== "undefined" &&
    /Mac|iPhone|iPod|iPad/i.test(navigator.userAgent)
  )
}

// Validate path to prevent open redirect vulnerabilities
export function isValidRelativePath(path: string) {
  return (
    path.startsWith("/") &&
    !path.startsWith("//") &&
    !path.includes(":") &&
    !path.includes("\\") &&
    // A percent-encoded backslash (%5c) is decoded by browsers into a
    // backslash, which several engines then treat like "/", turning
    // "/%5cevil.com" into a protocol-relative redirect that slips past the
    // backslash / "//" checks above.
    !/%5c/i.test(path) &&
    // Control characters and whitespace (raw CR/LF/TAB/space, 0x00-0x20,
    // DEL) can likewise be stripped or normalized by the browser into a
    // different target. A genuine app-relative path never contains them
    // un-encoded.
    // eslint-disable-next-line no-control-regex
    !/[\u0000-\u0020\u007F]/.test(path)
  )
}

export function extractEntityInfo(input: string) {
  // capture information in the format [name](type://id)
  // this regex contains 3 groups
  // 1. name - ([^\]]+)
  // 2. type - ([^:]+(?:::[^:]+)*)
  // 3. id - ([^)]+)
  if (!input) return { name: "" }
  const regex = /\[([^\]]+)]\(([^:]+(?:::[^:]+)*):\/\/([^)]+)\)/
  const match = regex.exec(input)

  if (match?.[1] && match[2] && match[3]) {
    return {
      name: match[1],
      type: match[2],
      id: match[3],
    }
  }
  return {
    name: input,
  }
}

export function avatarLetters(name: string | undefined | null, limit = 2) {
  if (!name) return ""
  const letters = name
    .split(" ")
    .map((item) => item.slice(0, 1))
    .join("")
  const initials = letters.length >= limit ? letters : name
  return initials.slice(0, limit).toUpperCase()
}

export function isAdmin(user: CurrentUser) {
  return hasTenantRole(user, "admin")
}

export function canAccessAdminPage(user: CurrentUser) {
  return isAdmin(user)
}

// accepts a json object and returns a query string
// { key1: "value1", key2: "value2" } -> "key1=value1&key2=value2"
// { key1: ["value1", "value2"] } -> "key1[]=value1&key1[]=value2"
// { key1: { subkey1: "value1", subkey2: ["value2", "value3"] } } -> "key1[subkey1]=value1&key1[subkey2][]=value2&key1[subkey2][]=value3"
// FIXME: {"b": [{"c": 1}, {"c": 2}]} -> "b[][c]=1&b[][c]=2" possibly using URLSearchParams
export function transformQueryParamsForGetRequest(
  queryParams: Record<string, unknown>,
  prefix = ""
): string {
  return Object.entries(queryParams)
    .map(([key, value]) => {
      const fullKey = prefix ? `${prefix}[${key}]` : key

      if (Array.isArray(value)) {
        return value
          .map((item) => `${fullKey}[]=${encodeURIComponent(item as string)}`)
          .join("&")
      } else if (typeof value === "object" && value !== null) {
        return transformQueryParamsForGetRequest(
          value as Record<string, unknown>,
          fullKey
        )
      } else if (value === undefined) {
        return ""
      } else {
        return `${fullKey}=${encodeURIComponent(value as string)}`
      }
    })
    .filter((param) => param !== "")
    .join("&")
}

export function formatAbsoluteTime(dateString: string): string {
  try {
    const date = parseISO(dateString)
    if (isValid(date)) {
      // Show the date in the user's browser timezone
      return format(date, "PPpp") // e.g., "Jan 1, 2024 at 2:30:45 PM"
    }
    return dateString
  } catch {
    return dateString
  }
}

export const CHECKIN_TIME_OPTIONS = (() => {
  const times = []
  for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += 30) {
      const hour12 = h % 12 === 0 ? 12 : h % 12
      const period = h < 12 || h === 24 ? "AM" : "PM"
      const minute = m === 0 ? "00" : "30"
      const timeString = `${hour12}:${minute} ${period}`
      const id = `${h.toString().padStart(2, "0")}${minute}`
      times.push({ id, label: timeString })
    }
  }
  return times
})()

/**
 * Formats a date to ISO format in date's timezone (YYYY-MM-DD)
 *
 * @param date - Date object
 * @returns ISO date string in date's timezone (e.g., "2024-01-15")
 */
export const formatDateISO = (date: Date): string => {
  return date.toLocaleDateString("en-CA")
}

export const formatRelativeTimeCompact = (dateString: string) => {
  const timeUnitMap: Record<string, string> = {
    " seconds": "now",
    " second": "now",
    " minutes": "m",
    " minute": "m",
    " hours": "h",
    " hour": "h",
    " days": "d",
    " day": "d",
    " weeks": "w",
    " week": "w",
    " months": "mo",
    " month": "mo",
    " years": "y",
    " year": "y",
  }

  let result = formatDistanceToNowStrict(new Date(dateString), {
    addSuffix: false,
  })

  // Remove "about " prefix
  result = result.replace("about ", "")

  // Special handling for days -> weeks conversion since formatDistanceToNowStrict doesn't use weeks
  const daysMatch = /^(\d+) days?$/.exec(result)
  if (daysMatch?.[1]) {
    const days = Number.parseInt(daysMatch[1], 10)
    if (days >= 7) {
      const weeks = Math.floor(days / 7)
      // Always show completed weeks for 7+ days (e.g., 10d = 1w, 15d = 2w)
      result = weeks === 1 ? "1 week" : `${weeks} weeks`
    }
  }

  // Replace time units using the mapping
  for (const [long, short] of Object.entries(timeUnitMap)) {
    result = result.replace(long, short)
  }

  // Handle the special case where we want to show just "now" for seconds
  if (result.includes("now")) {
    return "now"
  }

  return result
}

/** Makes keys NOT in TPartial required, keys in TPartial optional (can override) */
type PartiallyAppliedArgs<
  TArgs extends object,
  TPartial extends Partial<TArgs>,
> = Omit<TArgs, keyof TPartial & keyof TArgs> &
  Partial<Pick<TArgs, keyof TPartial & keyof TArgs>>

/** Partially apply arguments to a function that takes an object parameter
 * @knipignore
 */
export const partial = <
  TArgs extends Record<string, unknown>,
  TReturn,
  TPartial extends Partial<TArgs>,
>(
  fn: (args: TArgs) => TReturn,
  partialArgs: TPartial
): ((args: PartiallyAppliedArgs<TArgs, TPartial>) => TReturn) => {
  return (args) => fn({ ...partialArgs, ...args } as TArgs)
}
